import { useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Pill, CheckCircle, AlertTriangle, XCircle, Loader2, ScanSearch } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const ESITI = {
  esatta: { label: 'Corrispondenza esatta', classe: 'bg-emerald-100 text-emerald-700', Icona: CheckCircle },
  probabile: { label: 'Corrispondenza probabile', classe: 'bg-amber-100 text-amber-700', Icona: AlertTriangle },
  assente: { label: 'Nessuna corrispondenza', classe: 'bg-red-100 text-red-700', Icona: XCircle },
};

export default function ControlloAIFA() {
  const navigate = useNavigate();
  const [dati, setDati] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filtro, setFiltro] = useState('tutti');
  const [selezionata, setSelezionata] = useState(null);
  const [form, setForm] = useState({ principio_attivo: '', atc: '', forma_farmaceutica: '', nome_aifa: '' });
  const [salvataggio, setSalvataggio] = useState(false);

  const controlla = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/controllo-aifa');
      setDati(r.data);
      toast.success(`${r.data.totale} terapie controllate`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore controllo terapie'); }
    setLoading(false);
  }, []);

  const apri = (t) => {
    setSelezionata(t);
    setForm({
      principio_attivo: t.proposta?.principio_attivo || t.principio_attivo || '',
      atc: t.proposta?.atc || t.atc || '',
      forma_farmaceutica: t.proposta?.forma_farmaceutica || '',
      nome_aifa: t.proposta?.nome_aifa || '',
    });
  };

  const applica = async () => {
    setSalvataggio(true);
    try {
      await api.put(`/controllo-aifa/${selezionata.terapia_id}/applica`, form);
      toast.success('Corrispondenza confermata');
      setSelezionata(null);
      controlla();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore salvataggio'); }
    setSalvataggio(false);
  };

  const elenco = (dati?.terapie || []).filter(t => filtro === 'tutti' || t.esito === filtro);

  return (
    <div className="space-y-5 max-w-[1400px]" data-testid="controllo-aifa-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-[Cinzel] flex items-center gap-2">
            <ScanSearch className="w-6 h-6 text-brand" />Controllo terapie con la banca dati AIFA
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Le terapie importate dall'Excel sono testo libero: qui si dividono in corrispondenza esatta,
            probabile e assente, e la correzione viene confermata da una persona.
          </p>
        </div>
        <Button className="h-9 bg-brand hover:bg-brand-dark text-white" onClick={controlla} disabled={loading} data-testid="controlla-terapie-btn">
          {loading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Controllo...</> : <><ScanSearch className="w-4 h-4 mr-1" />Controlla terapie</>}
        </Button>
      </div>

      {dati && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card><CardContent className="p-4">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">Terapie attive</p>
              <p className="text-2xl font-bold text-slate-800" data-testid="aifa-totale">{dati.totale}</p>
            </CardContent></Card>
            {['esatta', 'probabile', 'assente'].map(k => {
              const { label, classe, Icona } = ESITI[k];
              return (
                <Card key={k} className="cursor-pointer hover:shadow-sm" onClick={() => setFiltro(k)}>
                  <CardContent className="p-4">
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider flex items-center gap-1"><Icona className="w-3 h-3" />{label}</p>
                    <p className="text-2xl font-bold text-slate-800" data-testid={`aifa-${k}`}>{dati.conteggi[k]}</p>
                    <Badge className={`${classe} text-[10px] mt-1`}>{k}</Badge>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500">Mostra</Label>
            <Select value={filtro} onValueChange={setFiltro}>
              <SelectTrigger className="h-8 w-56 text-xs" data-testid="aifa-filtro"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tutti">Tutte le terapie</SelectItem>
                <SelectItem value="assente">Senza corrispondenza</SelectItem>
                <SelectItem value="probabile">Corrispondenza probabile</SelectItem>
                <SelectItem value="esatta">Corrispondenza esatta</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-xs text-slate-400">{elenco.length} righe</span>
          </div>

          <div className="space-y-2">
            {elenco.slice(0, 300).map(t => {
              const { classe, Icona } = ESITI[t.esito];
              return (
                <Card key={t.terapia_id} className="hover:shadow-sm transition-shadow" data-testid={`aifa-riga-${t.terapia_id}`}>
                  <CardContent className="p-3 flex items-center gap-3 flex-wrap">
                    <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <Pill className="w-4 h-4 text-slate-500" />
                    </div>
                    <div className="flex-1 min-w-[220px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium text-slate-800">{t.farmaco}</p>
                        <Badge className={`${classe} text-[10px] gap-1`}><Icona className="w-3 h-3" />{t.esito}</Badge>
                        {t.aifa_confermato && <Badge variant="outline" className="text-[10px]">confermata</Badge>}
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {t.ospite_nome}{t.stanza ? ` - st. ${t.stanza}` : ''} · {t.dosaggio || 'dosaggio non indicato'} · {t.via || 'via non indicata'}
                      </p>
                      <p className="text-[11px] text-slate-500 mt-0.5">{t.motivo}</p>
                      {t.principio_attivo && <p className="text-[11px] text-brand">PA attuale: {t.principio_attivo}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      {t.proposta?.principio_attivo && (
                        <div className="text-[11px] text-slate-600 max-w-[220px]">
                          <p className="text-slate-400">Proposta</p>
                          <p className="font-medium">{t.proposta.principio_attivo}</p>
                          {t.proposta.categoria && <p className="text-slate-400">{t.proposta.categoria}</p>}
                        </div>
                      )}
                      <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => navigate(`/ospiti/${t.ospite_id}?tab=terapie`)}>
                        Cartella
                      </Button>
                      <Button size="sm" className="h-8 text-xs bg-brand hover:bg-brand-dark text-white"
                        onClick={() => apri(t)} data-testid={`aifa-correggi-${t.terapia_id}`}>
                        {t.esito === 'esatta' ? 'Conferma' : 'Correggi'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {!dati && !loading && (
        <div className="text-center py-16">
          <ScanSearch className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-400">Premi "Controlla terapie" per avviare il confronto con la banca dati AIFA</p>
        </div>
      )}

      <Dialog open={!!selezionata} onOpenChange={o => !o && setSelezionata(null)}>
        <DialogContent className="max-w-lg">
          {selezionata && (
            <>
              <DialogHeader><DialogTitle className="text-base">Conferma corrispondenza AIFA</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="bg-slate-50 rounded-lg p-3 text-xs space-y-0.5">
                  <p><span className="text-slate-400">Terapia:</span> <strong>{selezionata.farmaco}</strong> {selezionata.dosaggio}</p>
                  <p><span className="text-slate-400">Ospite:</span> {selezionata.ospite_nome}</p>
                  <p><span className="text-slate-400">Esito:</span> {selezionata.motivo}</p>
                </div>
                <div><Label className="text-xs">Principio attivo *</Label>
                  <Input className="h-9 text-xs" value={form.principio_attivo}
                    onChange={e => setForm(f => ({ ...f, principio_attivo: e.target.value }))} data-testid="aifa-pa" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Codice ATC</Label>
                    <Input className="h-9 text-xs" value={form.atc}
                      onChange={e => setForm(f => ({ ...f, atc: e.target.value }))} data-testid="aifa-atc" /></div>
                  <div><Label className="text-xs">Forma farmaceutica</Label>
                    <Input className="h-9 text-xs" value={form.forma_farmaceutica}
                      onChange={e => setForm(f => ({ ...f, forma_farmaceutica: e.target.value }))} /></div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setSelezionata(null)}>Annulla</Button>
                  <Button className="bg-brand hover:bg-brand-dark text-white" onClick={applica}
                    disabled={salvataggio} data-testid="aifa-applica">
                    {salvataggio ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}Conferma e salva
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
